import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { tokenStorage } from '@/lib/storage';
import { ApiError, bootstrapSession } from '@/lib/api-client';
import { authApi, type RequestUser } from './auth.api';

type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: RequestUser | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Boots in 'unknown' state. The access token is in-memory only, so a
 * page reload always starts with no access — the boot path tries to
 * mint a new one from the HttpOnly refresh cookie. On success: live
 * session. On failure (cookie expired / revoked / missing): the user
 * is unauthenticated and gets routed to /login.
 *
 * The router consumes `status` to:
 *  - render a full-screen spinner while 'unknown'
 *  - redirect to /login while 'unauthenticated'
 *  - render the app shell while 'authenticated'
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'unknown', user: null });

  // Boot path: try refresh — if the cookie's alive we get an access
  // token back, then /me confirms the live user. If refresh returns
  // null (no cookie / expired / revoked) we mark unauthenticated.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const access = await bootstrapSession();
      if (cancelled) return;
      if (!access) {
        setState({ status: 'unauthenticated', user: null });
        return;
      }
      try {
        const user = await authApi.me();
        if (!cancelled) setState({ status: 'authenticated', user });
      } catch (err: unknown) {
        if (cancelled) return;
        // /me failing after a successful refresh is exotic (race during
        // suspend? misconfigured user?). Conservatively unauthenticated.
        if (err instanceof ApiError) tokenStorage.clear();
        setState({ status: 'unauthenticated', user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen to forced-logout events fired by the api-client when refresh fails.
  useEffect(() => {
    function handler() {
      tokenStorage.clear();
      setState({ status: 'unauthenticated', user: null });
    }
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const session = await authApi.login(email, password);
    // The HttpOnly refresh cookie was set by the response — only the
    // access token (in-memory) is our concern client-side.
    tokenStorage.write({ accessToken: session.accessToken });
    const user = await authApi.me();
    setState({ status: 'authenticated', user });
  }, []);

  const logout = useCallback(async () => {
    tokenStorage.clear();
    setState({ status: 'unauthenticated', user: null });
    // Best effort — the server clears the cookies regardless, and
    // returns 204 even if no session existed.
    await authApi.logout().catch(() => undefined);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
